'use client';

/**
 * CostPlanDetailsPanel — Thin wrapper for MCP (CostPlan) details
 *
 * Fetches CostPlan data via costplan_details strategy (V2 data gateway).
 * Delegates rendering to the schema-driven DetailsPanel using "mcp" entity schema.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain projects
 */

import { DollarSign, Loader2, Pencil, Trash2, Download, FileSpreadsheet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useMcpActions } from '@/lib/hooks/use-mcp-actions';
import { DetailsPanel } from './details/details-panel';

interface CostPlanDetailsPanelProps {
  entityId: string | null;
}

export function CostPlanDetailsPanel({ entityId }: CostPlanDetailsPanelProps) {
  const detailsStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'detail'));
  const { data, isLoading } = useStrategyData<Record<string, unknown>>(
    detailsStrategy ?? '',
    { semantic_id: entityId ?? '' },
    !!entityId && !!detailsStrategy,
  );
  const { editMcp, deleteMcp, downloadMcp, downloadMcpScheduleXlsx } = useMcpActions();

  if (!entityId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <DollarSign className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Select a cost plan to see details</p>
      </div>
    );
  }

  const bffData = data?.data ?? {};
  const planName = (bffData.name ?? 'Cost Plan') as string;

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <DollarSign className="h-4 w-4 text-warning shrink-0" />
          <span className="text-sm font-semibold truncate">
            {isLoading ? 'Loading...' : planName}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {entityId && !isLoading && (
            <>
              <button
                type="button"
                onClick={() => void editMcp(entityId)}
                aria-label="Edit cost plan"
                className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => void downloadMcp(entityId, planName)}
                aria-label="Download cost plan PDF"
                className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => void downloadMcpScheduleXlsx(entityId, planName)}
                aria-label="Download cost plan Excel"
                className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => void deleteMcp(entityId, planName)}
                aria-label="Delete cost plan"
                className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          <Badge variant="secondary" className="text-[10px] px-1.5 shrink-0">
            Cost Plan
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-thin p-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Loader2 className="h-8 w-8 mb-2 animate-spin" />
            <p className="text-sm">Loading details...</p>
          </div>
        ) : (
          <DetailsPanel
            entityType="mcp"
            entityId={entityId}
            data={{ ...bffData, semantic_id: bffData.semantic_id ?? entityId } as Record<string, unknown>}
            isLoading={false}
          />
        )}
      </div>
    </div>
  );
}
